/**
 * CrossLineagePage — GAP-8: tenant-wide cross-asset lineage + impact.
 *
 * Differs from the existing `LineagePage.js` (model-only schema view)
 * by walking EVERY asset kind on the tenant — data sources, models,
 * entities, fields, calc measures, dashboards, widgets, reports, and
 * schedules — and letting you start the trace from any of them.
 *
 * Two halves:
 *   left  — asset picker with kind-grouped search
 *   right — visual graph with upstream + root + downstream columns
 *
 * Click any node to re-root. The "Impact Only" toggle hides upstream
 * (matches the dialog people see when confirming a delete).
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import {
  Network, Search, Loader2, AlertTriangle, Layers, FileText, BarChart3,
  Database, GitBranch, Calendar, Sigma, Zap, ArrowDown, ArrowUp, Eye,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const KIND_META = {
  data_source: { icon: Database,    color: 'text-cyan-400',    bg: 'bg-cyan-500/10 border-cyan-500/20',     label: 'Data Source' },
  model:       { icon: GitBranch,   color: 'text-indigo-400',  bg: 'bg-indigo-500/10 border-indigo-500/20', label: 'Semantic Model' },
  entity:      { icon: Layers,      color: 'text-violet-400',  bg: 'bg-violet-500/10 border-violet-500/20', label: 'Entity' },
  field:       { icon: Sigma,       color: 'text-blue-400',    bg: 'bg-blue-500/10 border-blue-500/20',     label: 'Field' },
  calc:        { icon: Zap,         color: 'text-amber-400',   bg: 'bg-amber-500/10 border-amber-500/20',   label: 'Calc Measure' },
  dashboard:   { icon: BarChart3,   color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', label: 'Dashboard' },
  widget:      { icon: BarChart3,   color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', label: 'Widget' },
  report:      { icon: FileText,    color: 'text-rose-400',    bg: 'bg-rose-500/10 border-rose-500/20',     label: 'Report' },
  schedule:    { icon: Calendar,    color: 'text-fuchsia-400', bg: 'bg-fuchsia-500/10 border-fuchsia-500/20', label: 'Schedule' },
};

function AssetPicker({ index, query, setQuery, onPick, active }) {
  const filtered = useMemo(() => {
    if (!index) return {};
    const q = query.trim().toLowerCase();
    const out = {};
    for (const [kind, items] of Object.entries(index.by_kind || {})) {
      const matches = q ? items.filter(it => (it.name || '').toLowerCase().includes(q) || (it.id || '').toLowerCase().includes(q)) : items;
      if (matches.length) out[kind] = matches;
    }
    return out;
  }, [index, query]);

  return (
    <div className="space-y-3" data-testid="lineage-picker">
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search assets…"
          className="h-9 pl-9 text-xs rounded-lg bg-muted/30 border-border/30"
          data-testid="lineage-search"
        />
      </div>
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
        {index?.total ?? 0} total assets
      </div>
      <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
        {Object.entries(filtered).map(([kind, items]) => {
          const meta = KIND_META[kind] || {};
          const Icon = meta.icon || Network;
          return (
            <div key={kind}>
              <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider mb-1 ${meta.color || 'text-muted-foreground'}`}>
                <Icon className="w-3 h-3" /> {meta.label || kind} <span className="text-muted-foreground/80">({items.length})</span>
              </div>
              <div className="space-y-0.5">
                {items.slice(0, 50).map(it => (
                  <button
                    key={it.nid}
                    onClick={() => onPick(kind, it.id)}
                    className={`w-full text-left text-xs px-2 py-1.5 rounded transition-colors truncate ${active?.nid === it.nid ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/20'}`}
                    data-testid={`pick-${kind}-${it.id}`}
                  >
                    {it.name}
                  </button>
                ))}
                {items.length > 50 && (
                  <div className="text-[9px] text-muted-foreground/80 px-2">…{items.length - 50} more — refine search</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NodeChip({ n, onPick }) {
  const meta = KIND_META[n.kind] || {};
  const Icon = meta.icon || Network;
  return (
    <button
      onClick={() => onPick(n.kind, n.id)}
      className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs border ${meta.bg || 'bg-muted/10 border-border/20'} hover:scale-[1.02] transition-transform`}
      title={`${meta.label || n.kind}: ${n.name}`}
      data-testid={`node-${n.kind}-${n.id}`}
    >
      <Icon className={`w-3 h-3 ${meta.color || 'text-muted-foreground'}`} />
      <span className="truncate max-w-[180px]">{n.name}</span>
    </button>
  );
}

function KindGroup({ kind, items, onPick }) {
  const meta = KIND_META[kind] || {};
  return (
    <div className="space-y-1.5">
      <div className={`text-[10px] uppercase tracking-wider ${meta.color || 'text-muted-foreground'}`}>
        {meta.label || kind} ({items.length})
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.slice(0, 25).map(n => <NodeChip key={n.nid} n={n} onPick={onPick} />)}
        {items.length > 25 && <span className="text-[10px] text-muted-foreground/80 self-center">+{items.length - 25}</span>}
      </div>
    </div>
  );
}

function LineageGraph({ data, mode, onPick }) {
  if (!data || data.error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-muted-foreground text-sm">
        <Network className="w-10 h-10 mb-3 opacity-30" />
        {data?.error || 'Pick an asset on the left to view its lineage'}
      </div>
    );
  }

  const root = data.start;
  const nodesByNid = {};
  (data.nodes || []).forEach(n => { nodesByNid[n.nid] = n; });

  const upstreamSet = new Set();
  const downstreamSet = new Set();
  const upEdges = data.upstream?.edges || [];
  const downEdges = data.downstream?.edges || data.edges || [];
  upEdges.forEach(e => upstreamSet.add(e.source));
  downEdges.forEach(e => downstreamSet.add(e.target));

  const groupUp = {};
  const groupDown = {};
  for (const nid of upstreamSet) {
    const n = nodesByNid[nid]; if (!n || nid === root.nid) continue;
    (groupUp[n.kind] = groupUp[n.kind] || []).push(n);
  }
  for (const nid of downstreamSet) {
    const n = nodesByNid[nid]; if (!n || nid === root.nid) continue;
    (groupDown[n.kind] = groupDown[n.kind] || []).push(n);
  }

  const rootMeta = KIND_META[root.kind] || {};
  const RootIcon = rootMeta.icon || Network;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-6 items-start">
      {mode !== 'impact' && (
        <div className="space-y-3" data-testid="upstream-column">
          <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
            <ArrowUp className="w-3.5 h-3.5" /> Upstream
            <span className="text-muted-foreground/80 font-normal">({data.upstream?.count ?? 0})</span>
          </div>
          {Object.keys(groupUp).length > 0
            ? Object.entries(groupUp).map(([kind, items]) => <KindGroup key={kind} kind={kind} items={items} onPick={onPick} />)
            : <div className="text-xs text-muted-foreground/80 italic">No upstream dependencies</div>}
        </div>
      )}

      <div className={`flex flex-col items-center self-center min-w-[200px] ${mode === 'impact' ? 'lg:col-start-1' : ''}`} data-testid="lineage-root">
        <div className={`flex flex-col items-center gap-2 px-4 py-3 rounded-xl border-2 ${rootMeta.bg || 'bg-primary/10 border-primary/30'}`}>
          <RootIcon className={`w-8 h-8 ${rootMeta.color || 'text-primary'}`} />
          <div className="text-center">
            <p className="text-[9px] uppercase tracking-wider text-muted-foreground">{rootMeta.label || root.kind}</p>
            <p className="text-sm font-heading font-semibold text-foreground">{root.name}</p>
          </div>
        </div>
      </div>

      <div className="space-y-3" data-testid="downstream-column">
        <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
          <ArrowDown className="w-3.5 h-3.5" /> Downstream
          <span className="text-muted-foreground/80 font-normal">({data.downstream?.count ?? data.total_consumers ?? 0})</span>
        </div>
        {Object.keys(groupDown).length > 0
          ? Object.entries(groupDown).map(([kind, items]) => <KindGroup key={kind} kind={kind} items={items} onPick={onPick} />)
          : <div className="text-xs text-muted-foreground/80 italic">No downstream consumers — safe to modify</div>}
      </div>
    </div>
  );
}

export default function CrossLineagePage() {
  const [index, setIndex] = useState(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('lineage'); // 'lineage' | 'impact'

  useEffect(() => {
    apiFetch(`${API}/api/lineage/index`, { method: 'GET' })
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data?.detail || 'Failed to load lineage index');
        setIndex(data);
      })
      .catch(e => toast.error(`Lineage index failed: ${e.message}`));
  }, []);

  const loadLineage = useCallback(async (kind, id) => {
    setLoading(true);
    setActive({ kind, id, nid: `${kind}::${id}` });
    setData(null);
    try {
      const path = mode === 'impact' ? 'impact' : 'asset';
      const { ok, data } = await apiFetch(`${API}/api/lineage/${path}/${kind}/${encodeURIComponent(id)}`, { method: 'GET' });
      if (!ok) throw new Error(data?.detail || 'Failed to load lineage');
      setData(data);
    } catch (e) {
      toast.error(`Lineage failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [mode]);

  // Re-load whenever the user toggles between Lineage and Impact (refetches
  // the same root but switches the endpoint). Intentionally NOT depending on
  // `loadLineage`/`active` to avoid an infinite re-fetch loop when those
  // values change inside the loader itself.
  useEffect(() => {
    if (active) {
      const path = mode === 'impact' ? 'impact' : 'asset';
      apiFetch(`${API}/api/lineage/${path}/${active.kind}/${encodeURIComponent(active.id)}`, { method: 'GET' })
        .then(({ ok, data }) => { if (ok) setData(data); });
    }
  }, [mode]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="cross-lineage-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Network className="w-7 h-7 text-primary" /> Lineage &amp; Impact
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Trace any asset across the whole workspace — data sources, models, fields, dashboards, schedules — and see what breaks before you change it.
          </p>
        </div>
        <div className="flex items-center gap-1 border border-border/30 rounded-xl p-0.5">
          {['lineage', 'impact'].map(m => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all ${mode === m ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/20'}`}
              data-testid={`mode-${m}`}
            >
              {m === 'lineage' ? <><Eye className="w-3 h-3" /> Full Lineage</> : <><AlertTriangle className="w-3 h-3" /> Impact Only</>}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        <aside className="glass-card rounded-2xl p-4">
          <AssetPicker index={index} query={query} setQuery={setQuery} onPick={loadLineage} active={active} />
        </aside>
        <main className="glass-card rounded-2xl p-6 min-h-[400px]">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
          ) : (
            <>
              {data && mode === 'impact' && data.start && (
                <div className="mb-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30" data-testid="impact-warning">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-amber-300">{data.summary}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        Review every dependent asset below before deleting or breaking-changing this {KIND_META[data.start.kind]?.label?.toLowerCase() || data.start.kind}.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <LineageGraph data={data} mode={mode} onPick={loadLineage} />
            </>
          )}
        </main>
      </div>
    </div>
  );
}
